/**
 * Premium "Logo Beam Streak" intro.
 *
 * Visual design:
 *  - Deep cinematic backdrop (Allyver primary + secondary radial blobs).
 *  - A bright accent-coloured beam slashes diagonally across the frame from
 *    upper-left to lower-right, leaving a soft glow trail.
 *  - The text reveals split-line, the eyebrow ("WELCOME TO THE FAMILY") rides
 *    in from the right with a thin underline that draws.
 *  - A subtle parallax on the brand-logo stamp (right corner) and animated
 *    grain overlay sell the "premium" feel without becoming busy.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  svgDataUrl, typeSize, weights, withAlpha, colors as themeColors, resolveBrandLogoSrc,
} from "../../reel/sceneShared.js";

export const logoBeamStreak: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const titleSize = typeSize(isVertical ? "hero" : "mega", ctx.format);
  const eyebrowSize = typeSize("sm", ctx.format) * 0.75;
  const subSize = typeSize("md", ctx.format);
  const text = inputs.text ?? "Neuer Allyver-Partner";
  const sub = inputs.subtext ?? "";
  const footer = (inputs.number ?? "").toString().trim();
  const hideLogo = inputs.extras?.hideLogo === true;
  const logoSrc = resolveBrandLogoSrc();

  const t = makeTl(clipStartSec);
  const beamId = `${clipPrefix}-beam`;
  const glowId = `${clipPrefix}-glow`;
  const eyeId = `${clipPrefix}-eye`;
  const eyeBarId = `${clipPrefix}-eyebar`;
  const titleId = `${clipPrefix}-title`;
  const titleTopId = `${clipPrefix}-title-top`;
  const titleBotId = `${clipPrefix}-title-bot`;
  const subId = `${clipPrefix}-sub`;
  const stampId = `${clipPrefix}-stamp`;
  const grainId = `${clipPrefix}-grain`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const stampHtml = hideLogo || !logoSrc
    ? ""
    : `<div id="${stampId}" style="position:absolute;top:80px;left:80px;display:flex;align-items:center;gap:14px;opacity:0;">
        <img src="${escapeHtml(logoSrc)}" alt="brand" style="height:${isVertical ? 56 : 64}px;width:auto;object-fit:contain;" />
        <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyebrowSize * 0.85}px;letter-spacing:6px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.88)};">Allyver</div>
      </div>`;

  const eyebrowHtml = sub
    ? `<div style="display:flex;align-items:center;gap:18px;justify-content:center;">
         <div id="${eyeBarId}" style="height:2px;width:0;background:${accent};box-shadow:0 0 12px ${withAlpha(accent, 0.7)};"></div>
         <div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyebrowSize}px;letter-spacing:7px;text-transform:uppercase;color:${accent};opacity:0;">${escapeHtml(sub)}</div>
       </div>`
    : "";

  // Split title into two visual lines so each can slide-in from opposite sides.
  const titleWords = text.split(/\s+/);
  const half = Math.ceil(titleWords.length / 2);
  const lineTop = titleWords.slice(0, half).join(" ");
  const lineBot = titleWords.slice(half).join(" ");

  const titleHtml = `<div id="${titleId}" style="display:flex;flex-direction:column;align-items:center;gap:6px;text-align:center;">
      <div id="${titleTopId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${titleSize * 0.78}px;line-height:0.95;letter-spacing:-3px;color:${themeColors.fg};text-shadow:0 0 60px ${withAlpha(accent, 0.55)};">${escapeHtml(lineTop)}</div>
      ${lineBot ? `<div id="${titleBotId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${titleSize * 0.78}px;line-height:0.95;letter-spacing:-3px;color:${accent};text-shadow:0 0 60px ${withAlpha(accent, 0.55)};">${escapeHtml(lineBot)}</div>` : ""}
    </div>`;

  const grainUrl = svgDataUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="280" height="280"><filter id="n"><feTurbulence baseFrequency="2" numOctaves="2" stitchTiles="stitch"/></filter><rect width="100%" height="100%" filter="url(%23n)" opacity="0.9"/></svg>`,
  );
  const grainHtml = `<div id="${grainId}" style="position:absolute;inset:0;pointer-events:none;background-image:${grainUrl};opacity:0.07;mix-blend-mode:overlay;"></div>`;

  // Beam wrapper carries the tilt (CSS); inner element receives the X tween
  // from GSAP — keeps the 8° rotation intact while the streak crosses.
  const beamHtml = `<div style="position:absolute;left:-30%;top:-15%;width:60%;height:160%;transform:rotate(8deg);mix-blend-mode:screen;pointer-events:none;">
      <div id="${beamId}" style="position:absolute;inset:0;background:linear-gradient(105deg, transparent 35%, ${withAlpha(themeColors.fg, 0.92)} 47%, ${withAlpha(accent, 0.95)} 50%, ${withAlpha(themeColors.fg, 0.92)} 53%, transparent 65%);filter:blur(6px);"></div>
    </div>
    <div id="${glowId}" style="position:absolute;inset:0;background:radial-gradient(80% 50% at 50% 50%, ${withAlpha(accent, 0.45)} 0%, transparent 60%);opacity:0;mix-blend-mode:screen;pointer-events:none;"></div>`;

  const html = `${stampHtml}
${beamHtml}
${grainHtml}
<div id="${clipPrefix}-pad" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:36px;padding:96px;">
  ${eyebrowHtml}
  ${titleHtml}
  ${footer ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize * 0.55}px;letter-spacing:1.5px;line-height:1.35;color:${withAlpha(themeColors.fg, 0.55)};text-align:center;max-width:${isVertical ? 720 : 1100}px;text-transform:uppercase;opacity:0;">— ${escapeHtml(footer)} —</div>` : ""}
</div>`;

  const init = [
    `gsap.set("#${beamId}", { x: "-120%" });`,
    `gsap.set("#${titleTopId}", { opacity: 0, y: 40, scale: 0.92 });`,
    lineBot ? `gsap.set("#${titleBotId}", { opacity: 0, y: -40, scale: 0.92 });` : "",
    footer ? `gsap.set("#${subId}", { opacity: 0 });` : "",
    sub ? `gsap.set("#${eyeId}", { opacity: 0, x: 30 });` : "",
    sub ? `gsap.set("#${eyeBarId}", { width: 0 });` : "",
    !hideLogo && logoSrc ? `gsap.set("#${stampId}", { opacity: 0, y: -16 });` : "",
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    // Beam sweep.
    t.to(`#${beamId}`, { x: "260%", duration: 1.05, ease: "raw:\"power3.inOut\"" }, 0.12),
    t.to(`#${glowId}`, { opacity: 1, duration: 0.35, ease: "raw:\"power2.out\"" }, 0.42),
    t.to(`#${glowId}`, { opacity: 0, duration: 0.65, ease: "raw:\"power2.in\"" }, 0.85),
    // Title lines slam in just after the beam crosses center.
    t.to(`#${titleTopId}`, { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.6)\"" }, 0.55),
  ];
  if (lineBot) {
    tweens.push(t.to(`#${titleBotId}`, { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.6)\"" }, 0.7));
  }
  if (sub) {
    tweens.push(t.to(`#${eyeBarId}`, { width: 60, duration: 0.45, ease: "raw:\"power2.out\"" }, 0.95));
    tweens.push(t.to(`#${eyeId}`, { opacity: 1, x: 0, duration: 0.42, ease: "raw:\"power2.out\"" }, 1.1));
  }
  if (footer) {
    tweens.push(t.to(`#${subId}`, { opacity: 0.9, duration: 0.4, ease: "raw:\"power2.out\"" }, 1.35));
  }
  if (!hideLogo && logoSrc) {
    // Stamp möglichst früh einblenden, damit das Inspector-Toggle „Logo in
    // dieser Scene" sofort sichtbares Feedback gibt — sonst wirkt es kaputt,
    // weil der Stamp die ersten ~1 s der Scene bei opacity:0 wartete.
    tweens.push(t.to(`#${stampId}`, { opacity: 1, y: 0, duration: 0.35, ease: "raw:\"power2.out\"" }, 0.05));
  }
  // Slow zoom on title for cinematic feel.
  tweens.push(t.to(`#${titleId}`, { scale: 1.04, duration: clipDurationSec - 0.6, ease: "raw:\"none\"" }, 0.55));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 8));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
